/**
 * Peptides — compounds, vials, doses, and the reconstitution calculator.
 *
 * A log and a calculator. Nothing here recommends a dose, a schedule, a
 * storage duration or a site; every number shown is either something the user
 * entered or arithmetic on it. Beyond-use dates are user-set, and the app
 * counts down to them without asserting what they should be.
 */
import { useMemo, useState } from 'preact/hooks';
import type { JSX } from 'preact';
import { Card, Empty, Field, Icons, Sheet, Stepper, fmt } from '../ui/bits';
import { SYRINGES, concentrationMgPerMl, dosesRemaining, planDose } from '../domain/reconstitution';
import { adherence, nextDueDay } from '../domain/schedule';
import { DEFAULT_SITES, siteRest } from '../domain/rotation';
import { compounds, deleteDose, notify, protocols, recentDoses, saveProtocol, saveVial, settings, today, vials } from '../store';
import { addDays, daysBetween, fromDay, nowIso } from '../lib/time';
import { newId } from '../lib/id';
import type { Vial } from '../data/schema';

export function Peptides(props: { onLogDose: () => void; onTrends: () => void }): JSX.Element {
  const comps = compounds.value;
  const [calcOpen, setCalcOpen] = useState(false);
  const [vialSheet, setVialSheet] = useState<Vial | 'new' | null>(null);

  return (
    <>
      {comps.map((c) => {
        const protocol = protocols.value.find((p) => p.compoundId === c.id);
        const doses = recentDoses.value.filter((d) => d.compoundId === c.id);
        const from = addDays(today.value, -27);
        const stats = protocol
          ? adherence(protocol, from, today.value,
              doses.filter((d) => !d.skipped).map((d) => d.day),
              doses.filter((d) => d.skipped).map((d) => d.day))
          : null;
        const due = protocol ? nextDueDay(protocol, today.value) : null;

        return (
          <Card key={c.id} title={c.name}>
            <div class="row-between">
              <div class="col">
                <span class="label">Dose</span>
                <span class="readout readout-lg">
                  {protocol ? fmtMass(protocol.doseMg, c.displayUnit) : '—'}
                </span>
              </div>
              <div class="col" style={{ alignItems: 'flex-end' }}>
                <span class="label">Next</span>
                <span class="readout readout-sm">
                  {due ? (due === today.value ? 'Today' : fromDay(due).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })) : 'As needed'}
                </span>
              </div>
            </div>

            <div class="row-between" style={{ marginTop: '10px' }}>
              <span class="label">Started on</span>
              <input class="input" type="date" style={{ width: 'auto', minHeight: '34px', padding: '4px 8px', fontSize: '13px' }}
                value={protocol?.startedOn ?? ''}
                aria-label={`${c.name} started on`}
                onChange={(e) => {
                  const v = (e.target as HTMLInputElement).value;
                  if (protocol) {
                    void saveProtocol({ ...protocol, ...(v ? { startedOn: v } : { startedOn: undefined }) });
                  } else if (v) {
                    // No schedule yet (an as-needed oral, say): the date still
                    // needs somewhere to live, so make a minimal protocol.
                    void saveProtocol({ id: newId(), compoundId: c.id, doseMg: 0, kind: 'asneeded', startDay: v, startedOn: v, active: true, createdAt: nowIso() });
                  }
                }} />
            </div>
            {stats && stats.scheduled > 0 ? (
              <>
                <div class="divider" />
                <div class="row-between">
                  <span class="label">Last 28 days</span>
                  <span class="unit">
                    {stats.taken}/{stats.scheduled} taken
                    {stats.skipped ? ` · ${stats.skipped} skipped` : ''}
                    {stats.missed.length ? ` · ${stats.missed.length} missed` : ''}
                  </span>
                </div>
              </>
            ) : null}
          </Card>
        );
      })}

      <Card
        title="Vials"
        action={<button class="btn btn-sm btn-ghost" onClick={() => setVialSheet('new')}>Add</button>}
        flush
      >
        {vials.value.length === 0 ? (
          <Empty mark={Icons.pin}>
            No vials yet. Add one with its amount and diluent to get unit calculations.
          </Empty>
        ) : (
          <div class="list">
            {vials.value.map((v) => <VialRow key={v.id} vial={v} onEdit={() => setVialSheet(v)} />)}
          </div>
        )}
      </Card>

      <Card title="Over time">
        <p class="note" style={{ marginBottom: '10px' }}>Weight, GI, sleep, HRV and more with every dose marked on the same axis.</p>
        <button class="btn btn-full" onClick={props.onTrends}>Open trends</button>
      </Card>

      <Card title="Site rotation">
        <SiteRest />
      </Card>

      <Card
        title="Recent doses"
        flush
        action={<button class="btn btn-sm btn-ghost" onClick={() => setCalcOpen(true)}>Calculator</button>}
      >
        {recentDoses.value.length === 0 ? (
          <Empty>Doses you log will appear here.</Empty>
        ) : (
          <div class="list">
            {recentDoses.value.slice(0, 12).map((d) => {
              const c = compounds.value.find((x) => x.id === d.compoundId);
              return (
                <div key={d.id} class="item">
                  <div class="col grow">
                    <span class="item-title">
                      {fmtMass(d.doseMg, c?.displayUnit ?? 'mg')}
                      {d.units !== undefined ? ` · ${fmt(d.units, 1)} u` : ''}
                    </span>
                    <span class="item-sub">
                      {fromDay(d.day).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                      {' · '}{c?.name ?? 'Compound'}{d.site ? ` · ${d.site}` : ''}
                      {d.note ? ` · ${d.note}` : ''}
                    </span>
                  </div>
                  <button class="btn btn-sm btn-ghost" onClick={() => void deleteDose(d.id)}>Delete</button>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      <button class="fab" onClick={props.onLogDose} aria-label="Log dose">{Icons.plus} Dose</button>

      {calcOpen ? <CalculatorSheet onClose={() => setCalcOpen(false)} /> : null}
      {vialSheet ? (
        <VialSheet vial={vialSheet === 'new' ? null : vialSheet} onClose={() => setVialSheet(null)} />
      ) : null}
    </>
  );
}

function VialRow(props: { vial: Vial; onEdit: () => void }): JSX.Element {
  const v = props.vial;
  const compound = compounds.value.find((c) => c.id === v.compoundId);
  const protocol = protocols.value.find((p) => p.compoundId === v.compoundId);
  const withdrawn = recentDoses.value
    .filter((d) => d.vialId === v.id && !d.skipped)
    .reduce((s, d) => s + d.doseMg, 0);

  const left = protocol ? dosesRemaining(v.contentMg, withdrawn, protocol.doseMg) : null;
  const conc = v.diluentMl ? concentrationMgPerMl(v.contentMg, v.diluentMl) : null;
  const budDays = v.beyondUseDate ? daysBetween(today.value, v.beyondUseDate) : null;

  return (
    <button class="item" onClick={props.onEdit}>
      <div class="col grow">
        <span class="item-title">
          {v.label ?? 'Vial'} · {compound?.name ?? 'Compound'}
        </span>
        <span class="item-sub">
          {fmt(v.contentMg, 2)} mg{v.diluentMl ? ` / ${fmt(v.diluentMl, 2)} mL` : ' · not reconstituted'}
          {conc ? ` · ${fmt(conc, 2)} mg/mL` : ''}
          {v.storage ? ` · ${v.storage}` : ''}
        </span>
      </div>
      <div class="col" style={{ alignItems: 'flex-end', gap: '4px' }}>
        {left !== null ? (
          <span class="readout readout-sm">{left}<span class="unit"> left</span></span>
        ) : null}
        {budDays !== null ? (
          <span class={`badge ${budDays < 0 ? 'is-due' : budDays <= 7 ? 'is-warn' : ''}`}>
            {budDays < 0 ? `${Math.abs(budDays)}d past` : `${budDays}d`}
          </span>
        ) : null}
      </div>
    </button>
  );
}

function SiteRest(): JSX.Element {
  const protocol = protocols.value[0];
  const sites = protocol?.sites ?? DEFAULT_SITES;
  const history = recentDoses.value.filter((d) => d.site).map((d) => ({ site: d.site!, day: d.day }));
  const rest = siteRest(sites, history, today.value);

  if (!history.length) {
    return <Empty>Once you log doses with a site, rest time per site shows here.</Empty>;
  }
  return (
    <div class="row wrap" style={{ gap: '6px' }}>
      {rest.map((r) => (
        <span key={r.site} class="chip">
          {r.site}
          <span class="dim">{r.restDays === null ? 'never' : `${r.restDays}d`}</span>
        </span>
      ))}
    </div>
  );
}

/** A standalone reconstitution calculator, for planning a vial before opening it. */
function CalculatorSheet(props: { onClose: () => void }): JSX.Element {
  const [vialMg, setVialMg] = useState(10);
  const [diluentMl, setDiluentMl] = useState(2);
  const [doseMg, setDoseMg] = useState(1.5);
  const [syringeId, setSyringeId] = useState(settings.value.defaultSyringeId);
  const syringe = SYRINGES[syringeId] ?? SYRINGES['u100-0.3']!;

  const plan = useMemo(
    () => planDose({ vialMg, diluentMl, doseMg, syringe }),
    [vialMg, diluentMl, doseMg, syringe.id],
  );

  return (
    <Sheet title="Reconstitution" onClose={props.onClose}>
      <Field label="Vial contains (mg)"><Stepper value={vialMg} onChange={setVialMg} step={1} min={0} dp={2} /></Field>
      <Field label="Diluent added (mL)"><Stepper value={diluentMl} onChange={setDiluentMl} step={0.5} min={0} dp={2} /></Field>
      <Field label="Dose (mg)"><Stepper value={doseMg} onChange={setDoseMg} step={0.05} min={0} dp={2} /></Field>
      <Field label="Syringe">
        <select class="select" value={syringeId} onChange={(e) => setSyringeId((e.target as HTMLSelectElement).value)}>
          {Object.values(SYRINGES).map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
        </select>
      </Field>

      {plan.ok ? (
        <div class="card">
          <div class="row-between" style={{ alignItems: 'flex-end' }}>
            <div class="col">
              <span class="label">Draw to</span>
              <span class="readout readout-xl" style={{ color: 'var(--coral)' }}>{fmt(plan.drawnUnits, 1)}<span class="unit" style={{ fontSize: '14px' }}> u</span></span>
            </div>
            <div class="col" style={{ alignItems: 'flex-end' }}>
              <span class="label">Concentration</span>
              <span class="readout readout-md">{fmt(plan.concentrationMgPerMl, 3)}<span class="unit"> mg/mL</span></span>
            </div>
          </div>
          <div class="divider" />
          <div class="row wrap" style={{ gap: '14px' }}>
            <span class="unit">{fmt(plan.drawnVolumeMl, 3)} mL</span>
            <span class="unit">delivers {fmt(plan.deliveredMg, 3)} mg</span>
            <span class="unit">{Math.floor(vialMg / doseMg)} doses/vial</span>
          </div>
          {!plan.exactOnMark ? (
            <p class="note note-warn">
              Exact is {fmt(plan.exactUnits, 2)} u; this barrel marks every {syringe.increment} u.
            </p>
          ) : null}
          {plan.exceedsCapacity ? (
            <p class="note note-bad">
              Exceeds the {syringe.capacityUnits} u barrel.
            </p>
          ) : null}
        </div>
      ) : (
        <div class="card">
          {plan.issues.map((i) => <p key={i} class="note">{i}</p>)}
        </div>
      )}
    </Sheet>
  );
}

function VialSheet(props: { vial: Vial | null; onClose: () => void }): JSX.Element {
  const comps = compounds.value;
  const v = props.vial;
  const [compoundId, setCompoundId] = useState(v?.compoundId ?? comps[0]?.id ?? '');
  const [label, setLabel] = useState(v?.label ?? '');
  const [contentMg, setContentMg] = useState(v?.contentMg ?? 10);
  const [diluentMl, setDiluentMl] = useState(v?.diluentMl ?? 2);
  const [storage, setStorage] = useState<Vial['storage']>(v?.storage ?? 'fridge');
  const [bud, setBud] = useState(v?.beyondUseDate ?? '');
  const [status, setStatus] = useState<Vial['status']>(v?.status ?? 'active');

  const conc = diluentMl > 0 && contentMg > 0 ? concentrationMgPerMl(contentMg, diluentMl) : null;

  const save = async () => {
    await saveVial({
      id: v?.id ?? newId(),
      compoundId,
      ...(label.trim() ? { label: label.trim() } : {}),
      contentMg,
      diluentMl: diluentMl > 0 ? diluentMl : null,
      ...(diluentMl > 0 ? { reconstitutedAt: v?.reconstitutedAt ?? nowIso() } : {}),
      openedAt: v?.openedAt ?? nowIso(),
      ...(bud ? { beyondUseDate: bud } : {}),
      storage,
      status,
      createdAt: v?.createdAt ?? nowIso(),
    });
    notify('Vial saved');
    props.onClose();
  };

  return (
    <Sheet
      title={v ? 'Edit vial' : 'Add vial'}
      onClose={props.onClose}
      footer={<button class="btn btn-primary btn-full" onClick={() => void save()}>Save vial</button>}
    >
      <Field label="Compound">
        <select class="select" value={compoundId} onChange={(e) => setCompoundId((e.target as HTMLSelectElement).value)}>
          {comps.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </Field>
      <Field label="Label">
        <input class="input" style={{ fontFamily: 'var(--sans)' }} value={label} placeholder="Vial 1"
          onInput={(e) => setLabel((e.target as HTMLInputElement).value)} />
      </Field>
      <Field label="Contains (mg)"><Stepper value={contentMg} onChange={setContentMg} step={1} min={0} dp={2} /></Field>
      <Field label="Diluent (mL)" hint={conc ? `${fmt(conc, 3)} mg/mL` : 'Zero means not reconstituted'}>
        <Stepper value={diluentMl} onChange={setDiluentMl} step={0.5} min={0} dp={2} />
      </Field>
      <Field label="Storage">
        <select class="select" value={storage} onChange={(e) => setStorage((e.target as HTMLSelectElement).value as Vial['storage'])}>
          <option value="fridge">Fridge</option>
          <option value="freezer">Freezer</option>
          <option value="room">Room</option>
        </select>
      </Field>
      <Field label="Beyond-use date" hint="Yours to set. The app only counts down to it.">
        <input class="input" type="date" value={bud} onInput={(e) => setBud((e.target as HTMLInputElement).value)} />
      </Field>
      <Field label="Status">
        <select class="select" value={status} onChange={(e) => setStatus((e.target as HTMLSelectElement).value as Vial['status'])}>
          <option value="sealed">Sealed</option>
          <option value="active">Active</option>
          <option value="spent">Spent</option>
          <option value="discarded">Discarded</option>
        </select>
      </Field>
    </Sheet>
  );
}

function fmtMass(mg: number, unit: 'mg' | 'mcg'): string {
  if (unit === 'mcg') return `${fmt(mg * 1000)} mcg`;
  return `${String(Number(mg.toFixed(3)))} mg`;
}
